/**
 * BoardSettingsModal Component
 *
 * Modal for configuring board settings including background customization,
 * board properties (title) and group member management.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BoardBackgroundSelector } from './BoardBackgroundSelector';
import { BoardPreferences } from '@/types/preferences.types';
import { useThemeColors } from '@/styles/colors/useThemeColors';
import { FadeIn } from '@/components/AnimatedElements';
import { Icon, IconName } from '@/icons';
import { AsyncButton, Input } from '@/components/ui';
import { useModalManager } from '@/hooks/useModalManager';
import { ModalId } from '@/types/modal-manager.types';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { Alert, useAlert } from '@/components/core/Alert';
import { useBoardQuery } from '@/hooks/queries';
import type {
  UpdateBoardBackgroundInput,
  UpdateBoardBackgroundOutput,
  UpdateBoardInput,
  UpdateBoardOutput,
  SaveBoardAsTemplateInput,
  SaveBoardAsTemplateOutput,
  CreateBoardStatusInput,
  CreateBoardStatusOutput,
  DeleteBoardStatusInput,
  DeleteBoardStatusOutput,
} from '@/types/generated';
import type { BoardStatusWithCards } from '@/types/board-config.types';

/**
 * Component props
 */
export interface BoardSettingsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Board ID */
  boardId: string;
  /** Current board preferences (optional) */
  currentPreferences?: BoardPreferences;
  /** Callback when preferences change */
  onPreferencesChange?: (prefs: BoardPreferences) => void;
  /** Current board title */
  boardTitle?: string;
  /** Whether this is a group board */
  isGroupBoard?: boolean;
  /** Current board owner ID */
  ownerId?: string;
  /** Template ID if the board was created from a template */
  templateId?: string;
  /** Whether user has edit permission */
  hasEditPermission?: boolean;
  /** Whether user is admin (for destructive actions) */
  isAdmin?: boolean;
  /** Callback when board is deleted */
  onBoardDeleted?: () => void;
}

/**
 * Board Settings Modal Component
 */
export function BoardSettingsModal({
  isOpen,
  onClose,
  boardId,
  currentPreferences,
  onPreferencesChange,
  boardTitle,
  isGroupBoard = false,
  templateId,
  hasEditPermission = false,
  isAdmin = false,
  onBoardDeleted,
}: BoardSettingsModalProps): JSX.Element {
  const colors = useThemeColors();
  const { openModal, closeModal } = useModalManager();

  const { execute: updateBoardBackground } = useOrchestrator<
    UpdateBoardBackgroundInput,
    UpdateBoardBackgroundOutput
  >(Action.UPDATE_BOARD_BACKGROUND);

  // Whether the user has made changes that need saving to the backend on close
  const [hasPendingUpdate, setHasPendingUpdate] = useState(false);

  // Always-current ref so handleClose sees the latest preferences without stale closure
  const currentPreferencesRef = useRef(currentPreferences);
  useEffect(() => {
    currentPreferencesRef.current = currentPreferences;
  }, [currentPreferences]);

  // Track modal open/close state
  useEffect(() => {
    if (isOpen) {
      openModal(ModalId.BOARD_SETTINGS);
      setHasPendingUpdate(false);
    } else {
      closeModal(ModalId.BOARD_SETTINGS);
    }
  }, [isOpen, openModal, closeModal]);

  /**
   * Handle background change from selector.
   */
  const handleBackgroundChange = async (
    background?: BoardPreferences['background']
  ) => {
    if (background !== undefined) {
      onPreferencesChange?.({
        background,
      });
      setHasPendingUpdate(true);
      return;
    }

    setHasPendingUpdate(false);
    onPreferencesChange?.({
      background: currentPreferences?.background,
    });
  };

  /**
   * Handle dialog close.
   */
  const handleClose = (open: boolean) => {
    if (!open) {
      const prefs = currentPreferencesRef.current;
      if (hasPendingUpdate && prefs?.background?.absolute_url) {
        updateBoardBackground({
          board_id: boardId,
          background_url: prefs.background.absolute_url,
          display_mode: prefs.background.display_mode,
        });
      }
      setHasPendingUpdate(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-2xl flex flex-col max-h-[90vh]"
        style={{ backgroundColor: colors.background }}
      >
        <DialogHeader className="flex-shrink-0 pb-2">
          <DialogTitle className="flex items-center gap-3">
            <Icon
              name={IconName.SETTINGS}
              size="lg"
              style={{ color: colors.primary }}
            />
            <div className="flex-1">
              <div
                className="text-xl font-semibold"
                style={{ color: colors.text.primary }}
              >
                Board Settings
              </div>
              <div
                className="text-sm mt-1"
                style={{ color: colors.text.secondary }}
              >
                Customize your board appearance and preferences
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto py-2 pr-3 pl-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/20 hover:[&::-webkit-scrollbar-thumb]:bg-white/30">
          {/* Template Lineage Indicator */}
          {templateId && (
            <FadeIn direction="up" delay={25} duration={0.5}>
              <div
                className="flex items-center gap-2 mb-4 px-3 py-2 rounded-md"
                style={{ backgroundColor: colors.offWhite }}
              >
                <Icon
                  name={IconName.COPY}
                  size="sm"
                  style={{ color: colors.text.secondary }}
                />
                <span
                  className="text-xs"
                  style={{ color: colors.text.secondary }}
                >
                  Created from a template
                </span>
              </div>
            </FadeIn>
          )}

          {/* Board Properties Section */}
          {hasEditPermission && (
            <FadeIn direction="up" delay={50} duration={0.5}>
              <BoardPropertiesSection
                boardId={boardId}
                boardTitle={boardTitle}
              />
            </FadeIn>
          )}

          {/* Board Background Section */}
          <FadeIn
            direction="up"
            delay={hasEditPermission ? (isGroupBoard ? 150 : 100) : 100}
            duration={0.5}
          >
            <div
              className={hasEditPermission ? 'mt-6 pt-6 border-t' : ''}
              style={
                hasEditPermission
                  ? { borderColor: colors.border.default }
                  : undefined
              }
            >
              <h3
                className="text-sm font-medium mb-4"
                style={{ color: colors.text.primary }}
              >
                Board Background
              </h3>
              <BoardBackgroundSelector
                boardId={boardId}
                currentBackground={currentPreferences?.background}
                onBackgroundChange={handleBackgroundChange}
              />
            </div>
          </FadeIn>

          {/* Manage Columns Section — admin only */}
          {isAdmin && (
            <FadeIn
              direction="up"
              delay={hasEditPermission ? (isGroupBoard ? 175 : 125) : 125}
              duration={0.5}
            >
              <ManageColumnsSection boardId={boardId} />
            </FadeIn>
          )}

          {/* Save as Template Section — hidden for boards created from a template */}
          {!templateId && (
            <FadeIn
              direction="up"
              delay={hasEditPermission ? (isGroupBoard ? 200 : 150) : 150}
              duration={0.5}
            >
              <SaveAsTemplateSection boardId={boardId} />
            </FadeIn>
          )}

          {/* Danger Zone — admin only */}
          {isAdmin && (
            <FadeIn
              direction="up"
              delay={hasEditPermission ? (isGroupBoard ? 250 : 200) : 200}
              duration={0.5}
            >
              <DangerZoneSection
                boardId={boardId}
                onBoardDeleted={() => {
                  onClose();
                  onBoardDeleted?.();
                }}
              />
            </FadeIn>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Board Properties section — editable title
 */
function BoardPropertiesSection({
  boardId,
  boardTitle,
}: {
  boardId: string;
  boardTitle?: string;
}) {
  const colors = useThemeColors();
  const [title, setTitle] = useState(boardTitle || '');
  const [error, setError] = useState<string | null>(null);

  const { execute: executeUpdateBoard, isLoading: isSaving } = useOrchestrator<
    UpdateBoardInput,
    UpdateBoardOutput
  >(Action.UPDATE_BOARD);

  // Reset local state when modal reopens with new data
  useEffect(() => {
    setTitle(boardTitle || '');
    setError(null);
  }, [boardTitle]);

  // Dirty tracking
  const isDirty = title.trim() !== (boardTitle || '');

  const isValid = title.trim().length > 0 && title.trim().length <= 100;

  const handleSave = async () => {
    if (!isDirty || !isValid) return;
    setError(null);

    const input: UpdateBoardInput = { board_id: boardId };
    if (title.trim() !== (boardTitle || '')) {
      input.title = title.trim();
    }
    const result = await executeUpdateBoard(input);

    if (result.code === ResponseCode.FORBIDDEN) {
      setError('You do not have permission to update this board');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon
          name={IconName.EDIT}
          size="md"
          style={{ color: colors.primary }}
        />
        <h3
          className="text-sm font-medium"
          style={{ color: colors.text.primary }}
        >
          Board Properties
        </h3>
      </div>
      <p className="text-xs mb-4" style={{ color: colors.text.secondary }}>
        Edit board title
      </p>

      <div className="space-y-3">
        {/* Board Title */}
        <div>
          <label
            className="text-xs font-medium mb-1 block"
            style={{ color: colors.text.secondary }}
          >
            Title
          </label>
          <Input
            type="text"
            value={title}
            onChange={e => {
              setTitle(e.target.value);
              setError(null);
            }}
            maxLength={100}
            disabled={isSaving}
            placeholder="Board title"
            style={{
              backgroundColor: colors.offWhite,
              color: colors.text.primary,
            }}
          />
          <div className="flex justify-end mt-1">
            <span className="text-xs" style={{ color: colors.text.secondary }}>
              {title.length}/100
            </span>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <p className="text-xs" style={{ color: colors.error }}>
            {error}
          </p>
        )}

        {/* Save Button */}
        <AsyncButton
          onClick={handleSave}
          disabled={!isDirty || !isValid || isSaving}
          isLoading={isSaving}
          icon={IconName.SAVE}
          loadingText="Saving..."
          size="sm"
        >
          Save Changes
        </AsyncButton>
      </div>
    </div>
  );
}

/**
 * Group Members section — add/remove board members
 */
/**
 * Save as Template section within BoardSettingsModal
 */
function SaveAsTemplateSection({ boardId }: { boardId: string }) {
  const colors = useThemeColors();
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { execute: executeSaveAsTemplate, isLoading: isSaving } =
    useOrchestrator<SaveBoardAsTemplateInput, SaveBoardAsTemplateOutput>(
      Action.SAVE_BOARD_AS_TEMPLATE
    );

  const isValid =
    templateName.trim().length > 0 && templateName.trim().length <= 100;

  const handleSave = async () => {
    if (!isValid) return;
    setError(null);

    const result = await executeSaveAsTemplate({
      board_id: boardId,
      template_name: templateName.trim(),
      template_description: templateDescription.trim() || undefined,
    });

    if (result.code === ResponseCode.SUCCESS) {
      setTemplateName('');
      setTemplateDescription('');
    } else if (result.code === ResponseCode.DUPLICATE_RESOURCE) {
      setError('A template with this name already exists');
    }
  };

  return (
    <div
      className="mt-6 pt-6 border-t"
      style={{ borderColor: colors.border.default }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon
          name={IconName.COPY}
          size="md"
          style={{ color: colors.primary }}
        />
        <h3
          className="text-sm font-medium"
          style={{ color: colors.text.primary }}
        >
          Save as Template
        </h3>
      </div>
      <p className="text-xs mb-4" style={{ color: colors.text.secondary }}>
        Save this board's columns as a reusable template
      </p>

      <div className="space-y-3">
        {/* Template Name */}
        <div>
          <Input
            type="text"
            value={templateName}
            onChange={e => {
              setTemplateName(e.target.value);
              setError(null);
            }}
            maxLength={100}
            disabled={isSaving}
            placeholder="Template name *"
            style={{
              backgroundColor: colors.offWhite,
              color: colors.text.primary,
              borderColor: error ? colors.error : undefined,
            }}
          />
          <div className="flex justify-end mt-1">
            <span className="text-xs" style={{ color: colors.text.secondary }}>
              {templateName.length}/100
            </span>
          </div>
        </div>

        {/* Template Description */}
        <textarea
          value={templateDescription}
          onChange={e => setTemplateDescription(e.target.value)}
          maxLength={1000}
          disabled={isSaving}
          rows={2}
          className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            backgroundColor: colors.offWhite,
            color: colors.text.primary,
          }}
          placeholder="Description (optional)"
        />

        {/* Error message */}
        {error && (
          <p className="text-xs" style={{ color: colors.error }}>
            {error}
          </p>
        )}

        {/* Save Button */}
        <AsyncButton
          onClick={handleSave}
          disabled={!isValid || isSaving}
          isLoading={isSaving}
          icon={IconName.COPY}
          loadingText="Saving..."
          size="sm"
        >
          Save as Template
        </AsyncButton>
      </div>
    </div>
  );
}

/**
 * Manage Columns section — CRUD for board statuses (admin only)
 */
function ManageColumnsSection({ boardId }: { boardId: string }) {
  const colors = useThemeColors();
  const alert = useAlert();
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { execute: executeAddColumn, isLoading: isAdding } = useOrchestrator<
    CreateBoardStatusInput,
    CreateBoardStatusOutput
  >(Action.CREATE_BOARD_STATUS);

  const { execute: executeDeleteColumn } = useOrchestrator<
    DeleteBoardStatusInput,
    DeleteBoardStatusOutput
  >(Action.DELETE_BOARD_STATUS);

  // Fetch board data to get statuses
  const { data: boardData } = useBoardQuery({ board_id: boardId });
  const statuses: BoardStatusWithCards[] = boardData?.statuses ?? [];

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setError(null);

    const nextPosition =
      statuses.length > 0 ? Math.max(...statuses.map(s => s.position)) + 1 : 0;

    const result = await executeAddColumn({
      board_id: boardId,
      name: trimmed,
      position: nextPosition,
      ...(newColor ? { color: newColor } : {}),
    });

    if (result.code === ResponseCode.SUCCESS) {
      setNewName('');
      setNewColor('');
    } else if (result.code === ResponseCode.DUPLICATE_RESOURCE) {
      setError('A column with this name already exists');
    }
  };

  const handleDelete = (status: BoardStatusWithCards) => {
    alert.show({
      type: 'warning',
      title: `Delete "${status.name}" column?`,
      message: `This will soft-delete the column and its ${status.cards.length} card(s). They can be restored later.`,
      buttons: [
        { label: 'Cancel', variant: 'outline', onClick: () => {} },
        {
          label: 'Delete',
          variant: 'destructive',
          onClick: async () => {
            setDeletingId(status.id!);
            await executeDeleteColumn({ status_id: status.id! });
            setDeletingId(null);
          },
        },
      ],
    });
  };

  return (
    <>
      <Alert {...alert.props} />
      <div
        className="mt-6 pt-6 border-t"
        style={{ borderColor: colors.border.default }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Icon
            name={IconName.LAYOUT_TEMPLATE}
            size="md"
            style={{ color: colors.primary }}
          />
          <h3
            className="text-sm font-medium"
            style={{ color: colors.text.primary }}
          >
            Manage Columns
          </h3>
        </div>
        <p className="text-xs mb-4" style={{ color: colors.text.secondary }}>
          Add or remove board columns (statuses)
        </p>

        {/* Existing statuses */}
        <div className="space-y-2 mb-4">
          {statuses.map(status => (
            <div
              key={status.id}
              className="flex items-center gap-2 px-3 py-2 rounded-md"
              style={{ backgroundColor: colors.offWhite }}
            >
              {status.color && (
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: status.color }}
                />
              )}
              <span
                className="flex-1 text-sm"
                style={{ color: colors.text.primary }}
              >
                {status.name}
              </span>
              <span className="text-xs" style={{ color: colors.text.tertiary }}>
                {status.cards.length} card{status.cards.length !== 1 ? 's' : ''}
              </span>
              <button
                onClick={() => handleDelete(status)}
                disabled={deletingId === status.id}
                className="p-1 rounded hover:bg-black/10 transition-colors"
                title={`Delete ${status.name}`}
              >
                <Icon
                  name={IconName.TRASH}
                  size="sm"
                  style={{
                    color: colors.error,
                    opacity: deletingId === status.id ? 0.4 : 0.7,
                  }}
                />
              </button>
            </div>
          ))}
          {statuses.length === 0 && (
            <p className="text-xs" style={{ color: colors.text.tertiary }}>
              No columns yet
            </p>
          )}
        </div>

        {/* Add new column */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              type="text"
              value={newName}
              onChange={e => {
                setNewName(e.target.value);
                setError(null);
              }}
              maxLength={50}
              disabled={isAdding}
              placeholder="New column name"
              style={{
                backgroundColor: colors.offWhite,
                color: colors.text.primary,
              }}
            />
          </div>
          <Input
            type="color"
            value={newColor || colors.primary}
            onChange={e => setNewColor(e.target.value)}
            disabled={isAdding}
            className="w-10 h-10 p-1 cursor-pointer"
            title="Column color"
          />
          <AsyncButton
            onClick={handleAdd}
            disabled={!newName.trim() || isAdding}
            isLoading={isAdding}
            icon={IconName.PLUS}
            size="sm"
          >
            Add
          </AsyncButton>
        </div>

        {error && (
          <p className="text-xs mt-2" style={{ color: colors.error }}>
            {error}
          </p>
        )}
      </div>
    </>
  );
}

/**
 * Danger Zone section — delete board (admin only)
 */
function DangerZoneSection({
  boardId,
  onBoardDeleted,
}: {
  boardId: string;
  onBoardDeleted: () => void;
}) {
  const colors = useThemeColors();
  const [error, setError] = useState<string | null>(null);
  const confirmAlert = useAlert();

  const { execute: executeDeleteBoard, isLoading: isDeleting } =
    useOrchestrator<{ board_id: string }, unknown>(Action.DELETE_BOARD);

  const handleDelete = async () => {
    setError(null);
    const result = await executeDeleteBoard({ board_id: boardId });

    if (result.code === ResponseCode.SUCCESS) {
      onBoardDeleted();
    } else if (result.code === ResponseCode.FORBIDDEN) {
      setError('You do not have permission to delete this board');
    }
  };

  return (
    <>
      <div className="mt-6 pt-6 border-t" style={{ borderColor: colors.error }}>
        <div className="flex items-center gap-2 mb-2">
          <Icon
            name={IconName.TRASH}
            size="md"
            style={{ color: colors.error }}
          />
          <h3 className="text-sm font-medium" style={{ color: colors.error }}>
            Danger Zone
          </h3>
        </div>
        <p className="text-xs mb-4" style={{ color: colors.text.secondary }}>
          Delete this board. It can be restored later by an admin.
        </p>

        {error && (
          <p className="text-xs mb-2" style={{ color: colors.error }}>
            {error}
          </p>
        )}

        <AsyncButton
          onClick={async () => {
            confirmAlert.show({
              type: 'warning',
              title: 'Delete Board',
              message:
                'Are you sure you want to delete this board? All cards in this board will also be soft-deleted. An admin can restore it later.',
              buttons: [
                { label: 'Cancel', onClick: () => {} },
                {
                  label: 'Delete Board',
                  onClick: handleDelete,
                  variant: 'destructive',
                },
              ],
            });
          }}
          isLoading={isDeleting}
          icon={IconName.TRASH}
          loadingText="Deleting..."
          size="sm"
          variant="destructive"
        >
          Delete Board
        </AsyncButton>
      </div>
      <Alert {...confirmAlert.props} />
    </>
  );
}
